import type { Permission } from "@dentos/contracts";
import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router";
import { ErrorState, Loading } from "./components/ui";
import { ApiError } from "./lib/api";
import { AppShell } from "./layouts/AppShell";
import { AuthLayout } from "./layouts/AuthLayout";
import { useSession } from "./lib/session";
import { AcceptInvitationPage, ChooseClinicPage, ForgotPasswordPage, LoginPage, ResetPasswordPage } from "./routes/auth";

// Feature screens are split into their own chunks so the login page stays small.
const page = (load: () => Promise<Record<string, ComponentType>>, name: string) => {
  const C = lazy(async () => ({ default: (await load())[name]! }));
  return (
    <Suspense fallback={<Loading />}>
      <C />
    </Suspense>
  );
};

/** Signed in, with a clinic selected (or a platform admin heading to /platform). */
function RequireClinic() {
  const { session, loading } = useSession();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!session) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!session.activeTenant) return <Navigate to={session.isPlatformAdmin && session.tenants.length === 0 ? "/platform" : "/choose-clinic"} replace />;
  return <AppShell />;
}

function RequirePlatformAdmin() {
  const { session, loading } = useSession();
  if (loading) return <Loading />;
  if (!session) return <Navigate to="/login" replace />;
  if (!session.isPlatformAdmin) return <Navigate to="/" replace />;
  return <AppShell platform />;
}

/** Hides a screen the user cannot use (the API would refuse it anyway). */
function Gate({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { can } = useSession();
  if (!can(permission)) return <ErrorState error={new ApiError(403, "forbidden", "Ask your clinic administrator if you need access.")} />;
  return <>{children}</>;
}

const gated = (permission: Permission, el: ReactNode) => <Gate permission={permission}>{el}</Gate>;

export const router = createBrowserRouter([
  // Public pages: no staff session, no app shell.
  { path: "/book/:slug", element: page(() => import("./routes/public/booking-page"), "BookingPage") },
  { path: "/portal/:slug", element: page(() => import("./routes/public/portal-page"), "PortalPage") },
  { path: "/product", element: page(() => import("./routes/public/product-page"), "ProductPage") },
  {
    element: <AuthLayout />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/signup", element: page(() => import("./routes/signup"), "SignupPage") },
      { path: "/reset-password", element: <ResetPasswordPage /> },
      { path: "/accept-invitation", element: <AcceptInvitationPage /> },
      { path: "/choose-clinic", element: <ChooseClinicPage /> },
    ],
  },
  {
    element: <RequireClinic />,
    children: [
      { path: "/", element: page(() => import("./routes/dashboard"), "DashboardPage") },
      { path: "/calendar", element: gated("appointments.view", page(() => import("./routes/calendar"), "CalendarPage")) },
      { path: "/queue", element: gated("appointments.view", page(() => import("./routes/queue"), "QueuePage")) },
      { path: "/waitlist", element: gated("appointments.view", page(() => import("./routes/waitlist"), "WaitlistPage")) },
      { path: "/patients", element: gated("patients.view", page(() => import("./routes/patients/list"), "PatientListPage")) },
      { path: "/patients/new", element: gated("patients.create", page(() => import("./routes/patients/form"), "PatientCreatePage")) },
      { path: "/patients/:id", element: gated("patients.view", page(() => import("./routes/patients/detail"), "PatientDetailPage")) },
      { path: "/patients/:id/edit", element: gated("patients.edit", page(() => import("./routes/patients/form"), "PatientEditPage")) },
      { path: "/visits/:id", element: gated("clinical.view", page(() => import("./routes/visits/visit"), "VisitPage")) },
      { path: "/diet-advice/:id/print", element: gated("clinical.view", page(() => import("./routes/patients/ayurveda/diet-print"), "DietPrintPage")) },
      { path: "/assessments/:id/print", element: gated("clinical.view", page(() => import("./routes/patients/ayurveda/case-sheet-print"), "CaseSheetPrintPage")) },
      { path: "/prescriptions/:id/print", element: gated("clinical.view", page(() => import("./routes/prescriptions/print"), "PrescriptionPrintPage")) },
      { path: "/messages", element: gated("communications.view", page(() => import("./routes/engagement/messages-page"), "MessagesPage")) },
      { path: "/tasks", element: page(() => import("./routes/engagement/tasks-page"), "TasksPage") },
      { path: "/automation", element: gated("communications.view", page(() => import("./routes/engagement/automation-page"), "AutomationPage")) },
      { path: "/labs", element: gated("labs.manage", page(() => import("./routes/operations/labs-page"), "LabsPage")) },
      { path: "/therapies", element: gated("therapy.view", page(() => import("./routes/therapy/therapy-page"), "TherapyPage")) },
      { path: "/pharmacy", element: gated("pharmacy.dispense", page(() => import("./routes/pharmacy/pharmacy-page"), "PharmacyPage")) },
      { path: "/inventory", element: gated("inventory.view", page(() => import("./routes/operations/inventory-page"), "InventoryPage")) },
      { path: "/leads", element: gated("crm.manage", page(() => import("./routes/crm/leads-page"), "LeadsPage")) },
      { path: "/treatment-follow-up", element: gated("crm.manage", page(() => import("./routes/crm/follow-up-page"), "TreatmentFollowUpPage")) },
      { path: "/campaigns", element: gated("campaigns.manage", page(() => import("./routes/crm/campaigns-page"), "CampaignsPage")) },
      { path: "/insurance", element: gated("billing.view", page(() => import("./routes/insurance/insurance-page"), "InsurancePage")) },
      { path: "/reports", element: page(() => import("./routes/reports-page"), "ReportsPage") },
      { path: "/billing", element: gated("billing.view", page(() => import("./routes/billing/billing-page"), "BillingPage")) },
      { path: "/invoices/new", element: gated("billing.create", page(() => import("./routes/billing/invoice-editor"), "InvoiceEditorPage")) },
      { path: "/invoices/:id", element: gated("billing.view", page(() => import("./routes/billing/invoice-detail"), "InvoiceDetailPage")) },
      { path: "/invoices/:id/edit", element: gated("billing.create", page(() => import("./routes/billing/invoice-editor"), "InvoiceEditorPage")) },
      { path: "/invoices/:id/print", element: gated("billing.view", page(() => import("./routes/billing/print"), "InvoicePrintPage")) },
      { path: "/payments/:id/receipt", element: gated("billing.view", page(() => import("./routes/billing/print"), "ReceiptPrintPage")) },
      { path: "/clinical-setup", element: gated("clinical.view", page(() => import("./routes/clinical-setup"), "ClinicalSetupPage")) },
      { path: "/doctors", element: gated("doctors.view", page(() => import("./routes/doctors/list"), "DoctorListPage")) },
      { path: "/doctors/:id", element: gated("doctors.view", page(() => import("./routes/doctors/detail"), "DoctorDetailPage")) },
      { path: "/services", element: gated("services.view", page(() => import("./routes/services"), "ServicesPage")) },
      { path: "/staff", element: gated("staff.view", page(() => import("./routes/staff"), "StaffPage")) },
      { path: "/roles", element: gated("staff.view", page(() => import("./routes/roles"), "RolesPage")) },
      { path: "/branches", element: gated("branches.view", page(() => import("./routes/branches"), "BranchesPage")) },
      { path: "/settings", element: gated("settings.view", page(() => import("./routes/settings"), "SettingsPage")) },
      { path: "/data", element: gated("data.export", page(() => import("./routes/data-page"), "DataPage")) },
      { path: "/records", element: gated("settings.manage", page(() => import("./routes/governance-page"), "GovernancePage")) },
      { path: "/integrations", element: gated("settings.manage", page(() => import("./routes/integrations-page"), "IntegrationsPage")) },
      { path: "/audit", element: gated("audit.view", page(() => import("./routes/audit"), "AuditPage")) },
      { path: "/account", element: page(() => import("./routes/account"), "AccountPage") },
    ],
  },
  {
    element: <RequirePlatformAdmin />,
    children: [
      { path: "/platform", element: page(() => import("./routes/platform"), "PlatformPage") },
      // A platform admin without a clinic still needs My account (e.g. to switch on two-step sign-in).
      { path: "/platform/clinics/:id", element: page(() => import("./routes/platform-clinic"), "PlatformClinicPage") },
      { path: "/platform/account", element: page(() => import("./routes/account"), "AccountPage") },
    ],
  },
  { path: "*", element: <Outlet />, children: [{ path: "*", element: <ErrorState error={new ApiError(404, "not_found", "This page does not exist.")} /> }] },
]);
